/**
 * Ad spend -> ROI. Plan: docs/crm-ad-spend-roi-plan.md §7.
 *
 * Cost per lead, per screened, per randomized, per ad object, with a
 * campaign -> ad set -> ad drill.
 *
 * THE RULE THIS COMPONENT EXISTS TO HONOUR. Production has 37 screened and 27
 * randomized contacts in total across six campaigns, so most cost cells sit on
 * a very small n and many sit on zero. Therefore:
 *   - every cost cell carries its own n, always, never behind a disclosure;
 *   - a zero denominator renders an EM-DASH, never "$0.00" (reads as free) and
 *     never a division blow-up (reads as a bug);
 *   - nothing is hidden behind a sample-size gate. A cost-per-randomized of
 *     n=1 is worth seeing AND worth labelling, in that order.
 * The server already returns null for an unknowable cost; this file must not
 * "helpfully" coalesce that to zero.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, ChevronRight, Clock, DollarSign, History, Loader2, RefreshCw, Users } from 'lucide-react';
import api from '../../../services/api';
import { fmtCurrency } from './StatBlocks';
import { secondaryBtnCls } from '../filters/styles';

type Level = 'study' | 'campaign' | 'adset' | 'ad';

/** The drill order. Owner request 2026-08-18: study is the root, because that is
 *  the unit the money is being judged against — a study may run several
 *  campaigns and the question is what the STUDY cost, not what a campaign did. */
const NEXT_LEVEL: Record<Level, Level | null> = {
  study: 'campaign', campaign: 'adset', adset: 'ad', ad: null,
};

export interface AdRow {
  level: Level;
  object_id: string;
  object_label: string;
  parent_id: string | null;
  study_id: string | null;
  study_label: string | null;
  study_mix: Array<{ study_id: string; slug: string }> | null;
  currency: string | null;
  /** null = no spend row exists for this object in this window (NOT $0.00). */
  spend: string | number | null;
  impressions: number;
  clicks: number;
  leads: number;
  screened: number;
  randomized: number;
  not_eligible: number;
  in_flight: number;
  cost_per_lead: string | number | null;
  cost_per_screened: string | number | null;
  cost_per_randomized: string | number | null;
  cohort_from: string | null;
  cohort_to: string | null;
  child_count: number;
  attributed: boolean;
}

interface Totals {
  spend: number; leads: number; screened: number; randomized: number;
  not_eligible: number; in_flight: number;
  /** False when NOT ONE row had a spend row — the total is unknown, not zero. */
  spend_known?: boolean;
  currency: string | null; currency_mixed: boolean;
  cost_per_lead: number | null; cost_per_screened: number | null; cost_per_randomized: number | null;
}

const EM_DASH = '—';

/** A cost cell: the money on top, the n it was divided by underneath. The n is
 *  not optional chrome — it is what stops a $600 cost-per-randomized computed
 *  from a single subject being read as a stable unit economic. */
function CostCell({ value, n, currency }: { value: string | number | null; n: number; currency: string | null }) {
  const unknowable = value === null || value === undefined || n <= 0;
  return (
    <td className="px-4 py-3 text-right text-sm tabular-nums text-gray-900 dark:text-gray-100">
      {unknowable ? (
        <span className="text-gray-400 dark:text-gray-500" title={n <= 0 ? 'No outcomes yet — a cost per outcome cannot be computed' : 'Not available'}>
          {EM_DASH}
        </span>
      ) : (
        <span className="font-medium">{fmtCurrency(Number(value), currency)}</span>
      )}
      <span className="ml-1 text-xs text-gray-400 dark:text-gray-500">n={n.toLocaleString()}</span>
    </td>
  );
}

function Row({
  row, depth, expanded, onToggle,
}: { row: AdRow; depth: number; expanded: boolean; onToggle: () => void }) {
  const canExpand = NEXT_LEVEL[row.level] !== null && row.child_count > 0;
  const mix = row.study_mix || [];
  return (
    <tr className="transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50">
      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
        <div className="flex items-center gap-1.5" style={{ paddingLeft: depth * 18 }}>
          {canExpand ? (
            <button
              type="button"
              onClick={onToggle}
              aria-label={expanded ? 'Collapse' : 'Expand'}
              aria-expanded={expanded}
              className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:bg-gray-700 dark:hover:text-gray-300"
            >
              <ChevronRight className={`h-3.5 w-3.5 transition-transform duration-150 ${expanded ? 'rotate-90' : ''}`} />
            </button>
          ) : (
            <span className="inline-block h-5 w-5 flex-shrink-0" />
          )}
          <span className={row.attributed ? '' : 'text-gray-500 dark:text-gray-400'}>{row.object_label}</span>
          {row.study_label && (
            <span className="rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">
              {row.study_label}
            </span>
          )}
          {/* Owner-confirmed 2026-08-18: every campaign is single-study, so a
              mix is a DATA-QUALITY ALARM (a mis-tagged URL or an undated
              repurpose), not a routine "serves two studies" display. */}
          {mix.length > 1 && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
              title={`Leads from this object resolve to ${mix.length} different studies: ${mix.map((m) => m.slug).join(', ')}. Every campaign is expected to serve exactly one study, so this usually means a mis-tagged URL or a campaign repurposed without dating the mapping.`}
            >
              <AlertTriangle className="h-3 w-3" />
              {mix.length} studies
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-right text-sm tabular-nums text-gray-900 dark:text-gray-100">
        {/* null = no crm.ad_spend_daily row for this object in this window at
            all, which is NOT the same claim as "$0.00 was spent". Rendering the
            two identically is what made studies with real, un-ingested history
            look free. A genuine 0.00 spend row still prints $0.00. */}
        {row.spend === null || row.spend === undefined ? (
          <span
            className="text-gray-400 dark:text-gray-500"
            title="No spend has been imported for this object in this date range — which is not the same as having spent nothing. Check the coverage line above."
          >
            {EM_DASH}
          </span>
        ) : (
          fmtCurrency(Number(row.spend), row.currency)
        )}
      </td>
      <td className="px-4 py-3 text-right text-sm tabular-nums text-gray-900 dark:text-gray-100">{Number(row.leads || 0).toLocaleString()}</td>
      <td className="px-4 py-3 text-right text-sm tabular-nums text-gray-900 dark:text-gray-100">{Number(row.screened || 0).toLocaleString()}</td>
      <td className="px-4 py-3 text-right text-sm tabular-nums text-gray-900 dark:text-gray-100">{Number(row.randomized || 0).toLocaleString()}</td>
      <CostCell value={row.cost_per_lead} n={Number(row.leads || 0)} currency={row.currency} />
      <CostCell value={row.cost_per_screened} n={Number(row.screened || 0)} currency={row.currency} />
      <CostCell value={row.cost_per_randomized} n={Number(row.randomized || 0)} currency={row.currency} />
    </tr>
  );
}

export default function AdSpendReport({
  from, to, studyId,
}: { from: string | null; to: string | null; studyId: string | null }) {
  const [rows, setRows] = useState<AdRow[]>([]);
  const [totals, setTotals] = useState<Totals | null>(null);
  const [children, setChildren] = useState<Record<string, AdRow[]>>({});
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [health, setHealth] = useState<{
    last_synced_at: string | null; enabled: boolean; configured: boolean;
    spend_from?: string | null; spend_to?: string | null; spend_days?: number;
    backfill_months?: number; backfill_chunks_done?: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [syncMsg, setSyncMsg] = useState<{ tone: 'ok' | 'warn' | 'err'; text: string } | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    if (studyId) p.set('study_id', studyId);
    return p;
  }, [from, to, studyId]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    setExpanded(new Set());
    setChildren({});
    const p = new URLSearchParams(qs);
    p.set('level', 'study');
    api
      .get(`/api/crm/analytics/ad-spend/performance?${p.toString()}`)
      .then((res) => {
        if (!alive) return;
        setRows(res.data?.rows || []);
        setTotals(res.data?.totals || null);
      })
      .catch((err) => {
        if (!alive) return;
        setError(err?.response?.data?.error || 'Failed to load ad spend');
      })
      .finally(() => { if (alive) setLoading(false); });
    // Phase 5: the stale-sync signal. Spend that stopped arriving looks exactly
    // like spend that was zero, so the age of the last successful sync is the
    // only thing that distinguishes them. Fail-soft — a health error must not
    // take the report down.
    api
      .get('/api/crm/analytics/ad-spend/health')
      .then((res) => { if (alive) setHealth(res.data || null); })
      .catch(() => { if (alive) setHealth(null); });

    return () => { alive = false; };
  }, [qs, reloadKey]);

  const toggle = useCallback((row: AdRow) => {
    const key = `${row.level}:${row.object_id}`;
    setExpanded((prev) => {
      const next2 = new Set(prev);
      if (next2.has(key)) { next2.delete(key); return next2; }
      next2.add(key);
      if (!children[key]) {
        const next = NEXT_LEVEL[row.level];
        if (!next) return next2;
        const p = new URLSearchParams(qs);
        p.set('level', next);
        p.set('parent_id', row.object_id);
        api
          .get(`/api/crm/analytics/ad-spend/performance?${p.toString()}`)
          .then((res) => setChildren((c) => ({ ...c, [key]: res.data?.rows || [] })))
          .catch(() => setChildren((c) => ({ ...c, [key]: [] })));
      }
      return next2;
    });
  }, [children, qs]);

  const runSync = useCallback(async () => {
    setSyncing(true);
    setSyncMsg(null);
    try {
      const res = await api.post('/api/crm/analytics/ad-spend/sync', { days: 7 });
      const d = res.data || {};
      // A 200 means the ATTEMPT completed, not that Meta was happy — the service
      // never rejects, it names what failed. So the failures array decides the
      // tone, not the HTTP status.
      if (d.failures?.length) {
        // Do NOT guess at a cause. The first version of this message always
        // blamed the access token, and on 2026-08-18 it said exactly that while
        // 175 ad objects and 37 spend rows had just landed — which PROVES the
        // token works. A confident wrong diagnosis sends the reader to the one
        // place the problem is not.
        const landed = (d.spend || 0) + (d.entities || 0);
        setSyncMsg({
          tone: 'warn',
          text: `Sync finished with problems: ${d.failures.join(', ')}. `
            + `${d.spend || 0} spend row(s) and ${d.entities || 0} ad object(s) landed. `
            + (landed > 0
              ? 'Other steps succeeded, so Meta accepted the token — the failure is on our side. '
                + 'The exact error is on the crm.ad_sync_runs row for the failed step.'
              : 'Nothing landed at all, which usually means the access token was rejected or the '
                + 'ad account is not assigned to it. The crm.ad_sync_runs row carries the exact error.'),
        });
      } else {
        setSyncMsg({
          tone: 'ok',
          text: `Sync complete: ${d.spend || 0} spend row(s) over ${d.window_days} days `
            + `(${d.from} to ${d.to}), ${d.entities || 0} ad object(s) named, `
            + `${d.resolved || 0} lead(s) resolved, ${d.mapped || 0} campaign mapping(s) proposed.`,
        });
      }
      setReloadKey((k) => k + 1);
    } catch (err: any) {
      const body = err?.response?.data;
      setSyncMsg({
        tone: body?.code === 'AD_SYNC_IN_FLIGHT' ? 'warn' : 'err',
        text: body?.error || 'Could not start the sync.',
      });
    } finally {
      setSyncing(false);
    }
  }, []);

  /**
   * §5.5 — pull the HISTORY, not just the trailing window.
   *
   * The reason this button exists is the whole of the owner's second complaint
   * on 2026-08-18. Until now the only thing that ever wrote spend was the
   * nightly tick's trailing 28-day re-pull, so "All time" spend meant "the last
   * 28 days" and every campaign that finished spending before that read $0.00.
   *
   * The route works newest-first until its deadline and reports what is left,
   * because thirteen months of daily rows across three levels is minutes of
   * paged Meta calls. Finished chunks are recorded and skipped, so pressing this
   * again resumes rather than redoing — and the nightly tick carries on by
   * itself, which is why the message says so instead of demanding another click.
   */
  const runBackfill = useCallback(async () => {
    setBackfilling(true);
    setSyncMsg(null);
    try {
      const res = await api.post('/api/crm/analytics/ad-spend/backfill', {});
      const d = res.data || {};
      const landed = `${d.spend || 0} spend row(s) over ${d.chunks_done || 0} of `
        + `${d.chunks_total || 0} chunk(s), covering back to ${d.from || 'the horizon'}.`;
      if (d.chunks_remaining > 0) {
        setSyncMsg({
          tone: 'warn',
          text: `Backfill in progress: ${landed} `
            + `${d.chunks_remaining} chunk(s) still to pull`
            + (d.throttled ? ' (Meta rate-limited this run, which is normal for a long pull)' : '')
            + '. Press it again to continue, or leave it — the nightly sync picks up '
            + 'where this stopped. Nothing already pulled is fetched twice.',
        });
      } else {
        setSyncMsg({
          tone: 'ok',
          text: `Backfill complete: ${landed} `
            + `${d.entities || 0} ad object(s) named, ${d.mapped || 0} campaign mapping(s) proposed.`,
        });
      }
      setReloadKey((k) => k + 1);
    } catch (err: any) {
      const body = err?.response?.data;
      setSyncMsg({
        tone: body?.code === 'AD_SYNC_IN_FLIGHT' ? 'warn' : 'err',
        text: body?.error || 'Could not start the backfill.',
      });
    } finally {
      setBackfilling(false);
    }
  }, []);

  const TONE = {
    ok: 'border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400',
    warn: 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-400',
    err: 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400',
  } as const;

  // Rendered in EVERY branch below, including the empty one. The empty state is
  // exactly when "Sync now" is most wanted, and an early return that dropped the
  // toolbar would hide the button at the only moment it matters.
  // How far back the money actually goes. This is the single most useful line on
  // the tab and it did not exist: "this study shows nothing" and "we have no
  // spend data from when this study ran" looked identical, and the second was
  // the true answer for most rows. Reads from crm.ad_spend_coverage, so it is the
  // real minimum over the whole table rather than the oldest row on a page.
  const coverage = (() => {
    if (!health?.spend_from || !health?.spend_to) return null;
    const fmt = (d: string) => new Date(`${d}T00:00:00`).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
    return `Spend covers ${fmt(health.spend_from)} – ${fmt(health.spend_to)}`
      + (health.spend_days ? ` (${health.spend_days.toLocaleString()} days).` : '.');
  })();

  const toolbar = (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {health?.last_synced_at
            ? `Spend last synced ${new Date(health.last_synced_at).toLocaleString()}.`
            : 'Spend has never synced.'}
          {coverage && <span className="ml-1">{coverage}</span>}
          {coverage && (
            <span className="ml-1">
              Anything that ran before that shows an em-dash, not a zero — press
              “Backfill history” to pull it.
            </span>
          )}
        </p>
        <div className="flex flex-shrink-0 items-center gap-2">
          <button
            type="button"
            className={secondaryBtnCls}
            onClick={runSync}
            disabled={syncing || backfilling}
            title="Pull the last 7 days of spend from Meta now, instead of waiting for the 09:30 UTC sync"
          >
            {syncing
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <RefreshCw className="h-3.5 w-3.5" />}
            {syncing ? 'Syncing…' : 'Sync now'}
          </button>
          <button
            type="button"
            className={secondaryBtnCls}
            onClick={runBackfill}
            disabled={syncing || backfilling}
            title={`Pull up to ${health?.backfill_months ?? 13} months of historical spend from Meta. `
              + 'Runs newest-first and resumes where it left off, so it is safe to press more than once.'}
          >
            {backfilling
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <History className="h-3.5 w-3.5" />}
            {backfilling ? 'Backfilling…' : 'Backfill history'}
          </button>
        </div>
      </div>
      {syncMsg && (
        <div className={`flex items-start gap-2 rounded-xl border p-4 text-sm ${TONE[syncMsg.tone]}`}>
          {syncMsg.tone === 'ok'
            ? <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0" />
            : <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />}
          <span>{syncMsg.text}</span>
        </div>
      )}
    </>
  );

  if (loading) {
    return (
      <div className="space-y-3">
        {toolbar}
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-3">
        {toolbar}
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          {error}
        </div>
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div className="space-y-3">
        {toolbar}
        <div className="rounded-xl border border-dashed border-gray-300 p-10 text-center dark:border-gray-600">
          <DollarSign className="mx-auto mb-4 h-12 w-12 text-gray-300 dark:text-gray-600" />
          <p className="mb-1 text-lg font-medium text-gray-900 dark:text-gray-100">No ad spend yet</p>
          <p className="mx-auto max-w-sm text-sm text-gray-500 dark:text-gray-400">
            Spend arrives from Meta on the nightly sync, or press “Sync now” for
            the last few days and “Backfill history” for the months before that.
            Until one has run, this report has nothing to divide by — the campaign
            breakdown on the Conversion tab works without it.
          </p>
        </div>
      </div>
    );
  }

  // Amber when spend has not landed for more than two nightly ticks. Two, not
  // one, so a single transient Meta failure does not cry wolf every morning.
  const staleness = (() => {
    if (!health) return null;
    // The gap this closes: with CRM_AD_ROI_ENABLED on but META_ADS_ENABLED off,
    // this tab renders in full with every cost an em-dash and NOTHING saying
    // why. Silence there reads as "these campaigns cost nothing".
    if (!health.enabled) {
      return 'Meta ad-spend syncing is switched off, so no spend has been imported. '
        + 'The leads and outcomes below are real; every cost column reads an em-dash '
        + 'because there is nothing to divide by.';
    }
    if (!health.configured) return 'Meta ad spend is switched on but no access token is configured, so no spend will ever arrive.';
    if (!health.last_synced_at) return 'No ad-spend sync has completed yet, so every cost column reads an em-dash.';
    const ageH = (Date.now() - new Date(health.last_synced_at).getTime()) / 3600000;
    if (ageH > 48) {
      return `The last successful ad-spend sync was ${Math.floor(ageH / 24)} days ago `
        + `(${new Date(health.last_synced_at).toLocaleDateString()}). Spend below may be stale, `
        + 'which makes every cost look lower than it is.';
    }
    return null;
  })();

  // Recursive, deliberately. The previous version hand-unrolled exactly three
  // levels; with study added as a root that would have silently refused to render
  // the fourth (ads), and the bug would have looked like "ads have no data".
  const renderLevel = (list: AdRow[], depth: number): JSX.Element[] => {
    const out: JSX.Element[] = [];
    for (const row of list) {
      const key = `${row.level}:${row.object_id}`;
      out.push(
        <Row key={key} row={row} depth={depth} expanded={expanded.has(key)} onToggle={() => toggle(row)} />,
      );
      if (expanded.has(key)) out.push(...renderLevel(children[key] || [], depth + 1));
    }
    return out;
  };

  return (
    <div className="space-y-3">
      {toolbar}

      {staleness && (
        <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-400">
          <Clock className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{staleness}</span>
        </div>
      )}

      {totals?.currency_mixed && (
        // §7.4: multi-currency is DETECTED AND REFUSED, never converted. Showing
        // a total would mean adding dollars to euros.
        <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-400">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>
            This range mixes more than one currency, so no combined total is shown.
            Per-row figures are still correct in their own currency.
          </span>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
            <thead className="bg-gray-50 dark:bg-gray-800/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Study / campaign / ad set / ad</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Spend</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Leads</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Screened</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Randomized</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Cost / lead</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Cost / screened</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Cost / randomized</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">{renderLevel(rows, 0)}</tbody>
            {totals && !totals.currency_mixed && (
              <tfoot className="border-t-2 border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50">
                <tr>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900 dark:text-gray-100">All studies</td>
                  <td className="px-4 py-3 text-right text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-100">
                    {/* Same rule as the rows: no spend row anywhere means the
                        total is unknown, not zero. */}
                    {totals.spend_known === false ? (
                      <span
                        className="font-normal text-gray-400 dark:text-gray-500"
                        title="No spend has been imported for this date range at all."
                      >
                        {EM_DASH}
                      </span>
                    ) : (
                      fmtCurrency(totals.spend, totals.currency)
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-100">{totals.leads.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-100">{totals.screened.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-100">{totals.randomized.toLocaleString()}</td>
                  <CostCell value={totals.cost_per_lead} n={totals.leads} currency={totals.currency} />
                  <CostCell value={totals.cost_per_screened} n={totals.screened} currency={totals.currency} />
                  <CostCell value={totals.cost_per_randomized} n={totals.randomized} currency={totals.currency} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <p className="flex items-start gap-1.5 text-xs text-gray-500 dark:text-gray-400">
        <Users className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
        <span>
          Rows are <strong>studies</strong>, expandable into the campaigns that sold
          them, then ad sets and ads. A study&rsquo;s spend is the sum of its
          campaigns&rsquo; spend and is never split between studies — Meta charges
          the campaign. Leads are attributed on <strong>first touch</strong> — the
          ad a person first arrived from, not the study they are being considered
          for now.
          Outcomes come from the stage history, so a lead counts once however
          many times it re-entered a stage. An em-dash means there are no
          outcomes to divide by yet, not a cost of zero — and an em-dash under
          <strong>Spend</strong> means no spend has been imported for that period,
          which is not the same as having spent nothing.
        </span>
      </p>
    </div>
  );
}
